"use client";

import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

import { isTypingTarget } from "@/lib/keyboard";
import { openNavSearch } from "@/lib/navigation/nav-search-open";

/**
 * Type-to-search over part of the nav. The first printable key pressed while
 * the pointer is over the element, or while focus is inside it, opens the ⌘K
 * search seeded with that character, so no keystroke is lost.
 *
 * On the side nav it ranks the shown menu's pages first (`pagesFirst`,
 * `menuLabel`): someone typing at a menu is naming a destination, and the
 * search covers every destination, including the ones that menu never lists,
 * so a per-menu filter would be both narrower and a second search to learn.
 * On the top bar's search field it opens the plain search, as a click would.
 *
 * Scoped to the element on purpose: the panel stays open for the whole visit,
 * so a page-wide listener would steal keystrokes from the page. Keys pressed
 * in a field, or with a modifier, pass through. Returns the props the element
 * takes (`ref`, `onMouseEnter`, `onMouseLeave`).
 *
 * @param {object} [options]
 * @param {boolean} [options.pagesFirst] Rank page links above content.
 * @param {string} [options.menuLabel] The menu whose pages break ties.
 * @param {React.RefObject} [options.ref] A ref the element already has.
 */
export function useNavTypeToSearch({ pagesFirst = false, menuLabel = "", ref } = {}) {
  const ownRef = useRef(null);
  const zoneRef = ref || ownRef;
  const hoveringRef = useRef(false);
  // Read through a ref so the listener is attached once, not on every render.
  const optionsRef = useRef({ pagesFirst, menuLabel });
  useLayoutEffect(() => {
    optionsRef.current = { pagesFirst, menuLabel };
  }, [pagesFirst, menuLabel]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (isTypingTarget(event.target)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key.length !== 1 || !/\S/.test(event.key)) return;
      const zone = zoneRef.current;
      const atZone =
        hoveringRef.current || Boolean(zone?.contains(document.activeElement));
      if (!atZone) return;

      event.preventDefault();
      openNavSearch({ query: event.key, ...optionsRef.current });
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [zoneRef]);

  const onMouseEnter = useCallback(() => {
    hoveringRef.current = true;
  }, []);
  const onMouseLeave = useCallback(() => {
    hoveringRef.current = false;
  }, []);

  return { ref: zoneRef, onMouseEnter, onMouseLeave };
}
